import { Check, CircleX, Info, TriangleAlert, type LucideIcon } from 'lucide-react';

/**
 * Tone — what a message means, and the one place that decides how it looks.
 *
 * Five things draw the same four kinds of message: Alert, Badge, Empty, the
 * toasts, and Field's inline error. Each of them used to answer the question
 * separately, and they had already drifted: the red tone was a `CircleX` in an
 * alert, an `OctagonX` in a toast and a `TriangleAlert` under a field, and it
 * was spelled `danger` in one enum and `error` in another.
 *
 * So a tone lives here rather than inside any one component — its name, the
 * surface it is drawn on, the mark that stands in for it, and whether a screen
 * reader should interrupt for it. A component picks a tone; it does not get to
 * decide what the tone is.
 *
 * `danger` is the one spelling, matching the `--danger-*` roles it draws on.
 * sonner's slot is called `error`; that is the library's API, translated once
 * in `sonner.tsx` rather than becoming a second name for the same thing.
 *
 * Deliberately not here: shadcn's `destructive`. That is a solid red fill for a
 * button that deletes something — an action, not a message — and it belongs to
 * the shadcn contract in Tier 3 rather than to this vocabulary.
 */

/** Loudest first, with the untinted default last. */
export const TONES = ['danger', 'warning', 'success', 'neutral'] as const;

export type Tone = (typeof TONES)[number];

/**
 * The Tier 2 roles each tone owns, kept in three parts rather than pre-joined:
 * not every component wants all three. A notice strip tints its title with the
 * tone; a large block tints only its border and fill and leaves the prose in
 * the ordinary ink.
 *
 * Every entry is a whole literal class name on purpose. Tailwind's scanner and
 * `scan:tokens` both work by searching source text, and neither can follow a
 * class name assembled at runtime.
 */
const parts: Record<Tone, { edge: string; wash: string; text: string }> = {
  danger: { edge: 'border-danger-edge', wash: 'bg-danger-wash', text: 'text-danger-text' },
  warning: { edge: 'border-warn-edge', wash: 'bg-warn-wash', text: 'text-warn-text' },
  success: { edge: 'border-success-edge', wash: 'bg-success-wash', text: 'text-success-text' },
  neutral: { edge: 'border-hairline', wash: 'bg-raised', text: 'text-ink' },
};

function mapTones(draw: (part: (typeof parts)[Tone]) => string): Record<Tone, string> {
  return Object.fromEntries(TONES.map((tone) => [tone, draw(parts[tone])])) as Record<Tone, string>;
}

/** Border and fill only — for a block whose own copy sets its colour. */
export const toneBox = mapTones((p) => `${p.edge} ${p.wash}`);

/** Border, fill and text: the notice surface, wash-and-edge rather than solid. */
export const toneSurface = mapTones((p) => `${p.edge} ${p.wash} ${p.text}`);

/**
 * The mark that stands in for the tone, so the meaning survives without colour.
 *
 * Three circles and one triangle. The triangle is warning's alone — it is the
 * shape that reads as "careful" at 16px with the colour taken away, and handing
 * it to danger as well would leave the two blocking tones with nothing to tell
 * them apart but hue.
 */
export const toneIcon: Record<Tone, LucideIcon> = {
  danger: CircleX,
  warning: TriangleAlert,
  success: Check,
  neutral: Info,
};

/**
 * Blocking tones interrupt; advisory ones wait their turn. A screen reader
 * treats `alert` as something to cut across whatever it is reading, which is
 * right for the two tones that stopped the user and wrong for the two that
 * did not.
 */
export const toneRole: Record<Tone, 'alert' | 'status'> = {
  danger: 'alert',
  warning: 'alert',
  success: 'status',
  neutral: 'status',
};
